import { IS_BROWSER } from 'config/appConfig'

export const DEVICE_TYPES = {
  DESKTOP: 'desktop',
  TABLET: 'tablet',
  MOBILE: 'mobile',
}

export const PREVIEW_URL_PARAMS = {
  PREVIEW: 'preview',
  IN_IFRAME: 'inIframe',
}

export const PREVIEW_NAMESPACE = 'ssr-resource'

/**
 * Detects the current device type based on screen width and user agent.
 * @returns {string} One of DEVICE_TYPES values
 */
export const detectDeviceType = () => {
  if (!IS_BROWSER) return DEVICE_TYPES.DESKTOP

  const screenWidth = window?.innerWidth
  const userAgent = navigator?.userAgent?.toLowerCase()
  const isMobileUserAgent =
    /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent
    )

  if (screenWidth <= 768 || isMobileUserAgent) {
    return DEVICE_TYPES.MOBILE
  } else if (screenWidth <= 1024) {
    return DEVICE_TYPES.TABLET
  }
  return DEVICE_TYPES.DESKTOP
}

/**
 * Generates the iframe preview URL with optional additional search params.
 * @param {Object} [additionalParams] - Extra search params to include (e.g., { pitchId: '123' })
 * @returns {string|undefined} The generated URL string
 */
export const generatePreviewIframeUrl = (additionalParams = {}) => {
  if (IS_BROWSER) {
    const currentUrl = new URL(
      window?.location?.origin + window?.location?.pathname
    )

    Object.entries(additionalParams).forEach(([key, value]) => {
      if (value) {
        currentUrl.searchParams.set(key, value)
      }
    })

    currentUrl.searchParams.set(PREVIEW_URL_PARAMS.IN_IFRAME, 'true')
    currentUrl.searchParams.set('preview', 'true')

    return currentUrl.toString()
  }
}

/**
 * Extracts the shop display name from a site response object.
 * Works for both mysite and PO site responses.
 * @param {Object} siteResponse - The shop header response (mysiteShopHeaderResponse or poSiteShopHeaderResponse)
 * @param {string} currentLocale - Locale string like 'en_US'
 * @returns {string} The resolved shop display name
 */
export const getShopDisplayName = (siteResponse, currentLocale) => {
  if (!siteResponse || !currentLocale) return ''

  const { user, market } = siteResponse || {}
  const currentRegion = currentLocale?.split('_')?.[0] ?? 'en'
  const localizedShopDetails = market?.lang?.[currentRegion]
  const shopNameMarket = localizedShopDetails?.storeName
  const shopName = user?.shopName

  return shopName || shopNameMarket?.trim() || ''
}

/**
 * Scrolls the iframe content when the preview mask receives a wheel event.
 * @param {HTMLIFrameElement} iframeElement - The iframe DOM element
 * @param {WheelEvent} event - The wheel event from the mask overlay
 */
export const scrollIframeContent = (iframeElement, event) => {
  if (!iframeElement) return

  event?.preventDefault()

  try {
    const iframeWindow = iframeElement?.contentWindow
    if (iframeWindow) {
      iframeWindow?.scrollBy(event?.deltaX || 0, event?.deltaY || 0)
    }
  } catch (error) {
    console.warn(
      'Cross-origin iframe scroll access restricted:',
      error?.message
    )
  }
}

/**
 * Checks if the current page is loaded inside a preview iframe.
 * @returns {boolean}
 */
export const isInsidePreviewIframe = () => {
  return new URLSearchParams(window?.location?.search).has(
    PREVIEW_URL_PARAMS.IN_IFRAME
  )
}
